import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ArrowsDownUp, X } from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import ArtistCard from "@/components/ArtistCard";
import { disciplines as mockDisciplines } from "@/data/mockData";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [disciplineSlug, setDisciplineSlug] = useState(
    params.get("discipline") || ""
  );
  const [subSlug, setSubSlug] = useState(params.get("sub") || "");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const { disciplines: liveDisciplines } = useBrand();
  const disciplines =
    liveDisciplines && liveDisciplines.length ? liveDisciplines : mockDisciplines;

  const selected = useMemo(
    () => disciplines.find((d) => d.slug === disciplineSlug) || null,
    [disciplineSlug, disciplines]
  );

  // Debounce the free-text search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Keep URL in sync so deep links to /explorar?discipline=musica&sub=jazz work
  useEffect(() => {
    const next = new URLSearchParams();
    if (disciplineSlug) next.set("discipline", disciplineSlug);
    if (subSlug) next.set("sub", subSlug);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disciplineSlug, subSlug]);

  // Fetch artists whenever filters change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qp = {};
    if (disciplineSlug) qp.discipline = disciplineSlug;
    if (subSlug) qp.sub = subSlug;
    if (debouncedQ) qp.q = debouncedQ;
    api
      .get("/users", { params: qp })
      .then(({ data }) => !cancelled && setArtists(data.users || []))
      .catch(() => !cancelled && setArtists([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [disciplineSlug, subSlug, debouncedQ]);

  const total = artists.length;
  const selectDiscipline = (slug) => {
    setDisciplineSlug(slug);
    setSubSlug(""); // reset sub when switching
  };
  const clearFilter = () => {
    setDisciplineSlug("");
    setSubSlug("");
  };

  return (
    <Layout wide>
      <div data-testid="explore-page">
        <header className="border-b border-[#27272A] p-4 md:p-6">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
            DIRECTORIO · {String(total).padStart(3, "0")} ARTISTAS
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">
            Descubre
            <br />
            <span className="text-[#FF3E00]">quienes crean</span>
          </h1>

          <div className="mt-8 flex flex-col md:flex-row gap-3">
            <div className="flex-1 flex items-center gap-3 border border-[#27272A] bg-[#0a0a0a] px-4 focus-within:border-[#FF3E00]">
              <MagnifyingGlass size={16} className="text-[#A1A1AA]" />
              <input
                data-testid="explore-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="buscar @handle · nombre · ciudad..."
                className="flex-1 bg-transparent py-3 font-mono text-sm focus:outline-none placeholder:text-[#525252]"
              />
            </div>
            <button className="flex items-center justify-center gap-2 border border-[#27272A] hover:border-[#FF3E00] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white">
              <ArrowsDownUp size={14} />
              Top followers
            </button>
          </div>

          {/* Discipline filter row */}
          {selected ? (
            <div
              data-testid="discipline-selected-row"
              className="mt-6 flex items-center gap-3 flex-wrap"
            >
              {/* Selected discipline pill (solid) */}
              <button
                data-testid={`filter-${selected.slug}-active`}
                onClick={clearFilter}
                className="group font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border bg-[#FF3E00] text-white border-[#FF3E00] inline-flex items-center gap-2"
                title="Quitar filtro"
              >
                {selected.name}
                <X size={11} weight="bold" className="opacity-60 group-hover:opacity-100" />
              </button>

              {/* Visual divider */}
              <span className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
                ·
              </span>

              {/* Subcategories as horizontally-placed chips */}
              {(selected.sub_categories || []).length === 0 ? (
                <span className="font-mono text-[10px] text-[#525252] tracking-wider uppercase">
                  · sin subcategorías ·
                </span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="sub-all"
                    onClick={() => setSubSlug("")}
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
                      !subSlug
                        ? "border-[#F4F4F5] text-white bg-[#121212]"
                        : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
                    }`}
                  >
                    Todos los géneros
                  </button>
                  {(selected.sub_categories || []).map((sc) => {
                    const active = subSlug === sc.slug;
                    return (
                      <button
                        key={sc.slug}
                        data-testid={`sub-${sc.slug}`}
                        onClick={() => setSubSlug(active ? "" : sc.slug)}
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
                          active
                            ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                            : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
                        }`}
                      >
                        {sc.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // No discipline selected → show ALL disciplines
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                data-testid="filter-todas"
                onClick={clearFilter}
                className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border bg-[#FF3E00] text-white border-[#FF3E00]"
              >
                TODAS
              </button>
              {disciplines.map((d) => (
                <button
                  key={d.slug}
                  data-testid={`filter-${d.slug}`}
                  onClick={() => selectDiscipline(d.slug)}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5] transition-colors"
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}
        </header>

        <section className="p-4 md:p-6">
          {loading ? (
            <div className="py-24 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
              cargando…
            </div>
          ) : total === 0 ? (
            <div className="py-24 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
                · sin resultados ·
              </div>
              <p className="font-display text-3xl mt-4 text-[#525252]">
                Nadie por aquí.
              </p>
              {subSlug ? (
                <button
                  onClick={() => setSubSlug("")}
                  className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 border border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white"
                >
                  Quitar filtro de género
                </button>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              {artists.map((a, i) => (
                <ArtistCard key={a.id} artist={a} index={i} />
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
